import { useSelector } from 'react-redux';
import { FormInput } from '~/components/FormComponents/FormInput.js';
import { useGeneralInfoAssetManagerForm } from './hooks/useGeneralInfoAssetManagerForm.ts';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Grid } from '@mui/material';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.js';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.js';
import { useAddressQuery } from '~/queries/useAddressQuery.js';
import { AddressInfo } from '../../components/FormComponents/AddressInfo.js';
import { DocumentInfo } from '../../components/FormComponents/DocumentInfo.tsx';
import { PhoneList } from '../../components/FormComponents/PhoneList.tsx';
import { EmailList } from '../../components/FormComponents/EmailList.tsx';
import { TGeneralInfoAssetManager } from './types.ts';
import { errorMessages } from '~/constants/errorMessages.js';
import { updateFormAssetManager } from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  setIdentificationError,
  updateFormData,
  updateFormDataBack,
} from '~/store/identification-form/slices.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { cloneDeep } from 'lodash';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { B2fullCheck, trimValues } from '../../utils.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { selectTokenData } from '~/store/users/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';

interface IGeneralInfoAssetManager {
  onNextStep: () => void;
}

export const GeneralInfoAssetManager = ({ onNextStep }: IGeneralInfoAssetManager) => {
  const dispatch = useAppDispatch();
  const form = useGeneralInfoAssetManagerForm();
  const { isDirty, isSubmitting } = form.formState;
  const { countryOptions } = useAddressQuery();
  const { managerId, appId } = useSelector(selectTokenData) || {};

  const onSubmit = (isDirty: boolean) => async (values: TGeneralInfoAssetManager) => {
    if (isDirty) {
      try {
        const contactNumbers = values.contactNumbers.map((phone) => {
          const phoneNumber = phone?.replace(/\D/g, '') ?? '';
          return phoneNumber.length < 4 ? null : phone;
        });

        const generalInfoValues = {
          ...values,
          contactNumbers: contactNumbers.every((phone) => phone === null) ? null : contactNumbers,
        };

        const data = {
          generalInfo: trimValues(generalInfoValues),
          applicationId: +appId,
        };

        await dispatch(
          updateFormAssetManager({
            data,
            reportId: IdentificationFormSteps.generalInfoAssetManager,
            managerId,
            applicationId: appId,
          })
        );
        await B2fullCheck(appId, IdentificationFormSteps.generalInfoAssetManager);
        const formData = {
          field: IdentificationFormStepNames.generalInfo as keyof TIdentificationForm,
          data: cloneDeep(values),
        };
        dispatch(updateFormData(formData));
        dispatch(updateFormDataBack(formData));

        onNextStep();
      } catch (error) {
        console.error(error);
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    } else {
      onNextStep();
    }
  };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        <Grid container spacing={3}>
          <FormGridItem>
            <FormInput
              name="lastName"
              label="Прізвище"
              placeholder="Введіть повне прізвище"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput name="firstName" label="Ім'я" placeholder="Введіть повне ім'я" fullWidth />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="middleName"
              label="По батькові (за наявності)"
              placeholder="Введіть по батькові (за наявності)"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormDateInput
              name="birthDate"
              label="Дата народження"
              placeholder="Введіть дату народження"
              isCurrentMaxDate
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="identificationNumber"
              label="РНОКПП (за наявності)"
              placeholder="Введіть РНОКПП"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="foreignTaxNumber"
              label="Номер платника податків-для нерезидентів (за наявності)"
              placeholder="Введіть номер платника податків-для нерезидентів"
              fullWidth
            />
          </FormGridItem>
          <AddressInfo isPersonAddress />
          <Grid size={{ xs: 0, sm: 0, md: 6 }} />
          <FormGridItem>
            <FormAutocomplete
              name="citizenshipCountry"
              options={countryOptions}
              inputProps={{
                placeholder: 'Оберіть країну громадянства',
              }}
              label="Країна громадянства"
            />
          </FormGridItem>
          <FormGridItem>
            <FormAutocomplete
              name="residenceCountry"
              options={countryOptions}
              inputProps={{
                placeholder: 'Оберіть країну резидентства',
              }}
              label="Країна резидентства"
            />
          </FormGridItem>
          <FormGridItem>
            <FormAutocomplete
              name="birthplaceCountry"
              options={countryOptions}
              inputProps={{
                placeholder: 'Оберіть країну народження',
              }}
              label="Країна народження"
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="birthPlace"
              label="Місце народження"
              placeholder="Введіть місце народження"
              fullWidth
              inputProps={{
                maxLength: 200,
                minLength: 1,
              }}
            />
          </FormGridItem>
          <DocumentInfo
            citizenshipCountryPath="citizenshipCountry"
            residenceCountryPath="residenceCountry"
          />
          <PhoneList disabled />
          <EmailList onlyOneField disabled />
          <Grid size={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Продовжити
            </Button>
          </Grid>
        </Grid>
        {isSubmitting && <FullPageLoader />}
      </Box>
    </FormProvider>
  );
};
